import React, { useState, useEffect, useMemo, useCallback } from "react";
import { supabase } from "../supabaseClient";

const DashboardMap = ({ 
  user, 
  path = [], 
  filterType, 
  setFilterType, 
  setSelectedUser, 
  selectedUser, 
  users,
  handleHide,
  fromDate,      // Added prop
  setFromDate,    // Added prop
  toDate,        // Added prop
  setToDate      // Added prop
}) => {
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isOpen, setIsOpen] = useState(false);

  const fetchSessions = useCallback(async () => {
    if (!selectedUser) return;
    setLoading(true);

    console.log("🛠️ Dashboard Panel is fetching logs for Auth ID:", selectedUser);
const { data, error } = await supabase
  .from("user_location_history")
  .select("*")
  .eq("auth_id", selectedUser)
  .order("created_at", { ascending: false });

if (error) {
  console.error("🚨 Dashboard Panel DB Error:", error.message);
} else {
  console.log("✅ Dashboard Panel loaded rows:", data.length);
}

    if (!error) setSessions(data || []);
    setLoading(false);
  }, [selectedUser]);

  useEffect(() => {
    fetchSessions();
  }, [fetchSessions]);

  const totalSessions = new Set(sessions.map((s) => new Date(s.created_at).toDateString())).size;
  
  const totalDistanceKm = useMemo(() => {
    if (path.length < 2 || !window.google?.maps?.geometry?.spherical) return "0.00";
    let totalMeters = 0;
    for (let i = 0; i < path.length - 1; i++) {
      if (!path[i]?.lat || !path[i+1]?.lat) continue;
      const start = new window.google.maps.LatLng(path[i].lat, path[i].lng);
      const end = new window.google.maps.LatLng(path[i+1].lat, path[i+1].lng);
      totalMeters += window.google.maps.geometry.spherical.computeDistanceBetween(start, end);
    }
    return (totalMeters / 1000).toFixed(2);
  }, [path]);

  return (
    <>
      <button onClick={() => setIsOpen(!isOpen)} style={floatingButtonStyle}>
        {isOpen ? "✖ Close" : "📊 Activity Dashboard"}
      </button>

      {isOpen && (
        <div style={panelStyle}>
          <h3 style={{ margin: "0 0 15px 0", color: "#333", fontSize: '18px' }}>Control Center</h3>

          {/* SECTION 1: USER SELECTION (Admin Only) */}
          {user?.role === "super_admin" && (
            <div style={sectionStyle}>
              <label style={labelStyle}>Tracking User</label>
              <select
                value={selectedUser}
                onChange={(e) => setSelectedUser(e.target.value)}
                style={inputStyle}
              >
                <option value={user.auth_id}>Me (Self)</option>
                {users.filter(u => u.auth_id !== user.auth_id).map((u) => (
                  <option key={u.id} value={u.auth_id}>{u.name}</option>
                ))}
              </select>
            </div>
          )}

          {/* SECTION 2: TIME FILTERS */}
          <div style={sectionStyle}>
            <label style={labelStyle}>Time Range</label>
            <div style={buttonGroupGrid}>
              {['last', 'today', '7days', 'custom'].map((type) => (
                <button
                  key={type}
                  onClick={() => setFilterType(type)}
                  style={filterButtonStyle(filterType === type)}
                >
                  {type === 'last' ? 'Last Point' : type.charAt(0).toUpperCase() + type.slice(1)}
                </button>
              ))}
            </div>

            {/* CUSTOM DATE INPUTS (Only shows if 'custom' is selected) */}
            {filterType === "custom" && (
              <div style={{ display: "flex", gap: "8px", marginTop: "10px" }}>
                <div style={{ flex: 1 }}>
                  <small style={labelStyle}>From</small>
                  <input 
                    type="date" 
                    value={fromDate} 
                    onChange={(e) => setFromDate(e.target.value)} 
                    style={{ ...inputStyle, width: '100%', padding: '5px' }} 
                  />
                </div>
                <div style={{ flex: 1 }}>
                  <small style={labelStyle}>To</small>
                  <input 
                    type="date" 
                    value={toDate} 
                    onChange={(e) => setToDate(e.target.value)} 
                    style={{ ...inputStyle, width: '100%', padding: '5px' }} 
                  />
                </div>
              </div>
            )}

            <button onClick={handleHide} style={clearButtonStyle}>Clear Map Path</button>
          </div>

          {/* SECTION 3: STATS CARDS */}
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "10px", marginBottom: "20px" }}>
            <div style={statCardStyle}>
              <small style={labelStyle}>Distance</small>
              <div style={valueStyle}>{totalDistanceKm} km</div>
            </div>
            <div style={statCardStyle}>
              <small style={labelStyle}>Days Active</small>
              <div style={valueStyle}>{totalSessions}</div>
            </div>
          </div>

          {/* SECTION 4: HISTORY LIST */}
          <h4 style={{ fontSize: "14px", marginBottom: "10px", color: "#666" }}>Recent Activity</h4>
          <div style={tableWrapperStyle}>
            {loading ? (
               <p style={{fontSize: '12px', padding: '10px'}}>Loading logs...</p>
            ) : (
              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "12px" }}>
                <tbody style={{backgroundColor: '#fff'}}>
                  {sessions.slice(0, 5).map((s) => (
                    <tr key={s.id} style={{ borderBottom: "1px solid #eee" }}>
                      <td style={{padding: '8px'}}>
                        {new Date(s.created_at).toLocaleDateString([], { month: 'short', day: 'numeric' })}<br/>
                        <span style={{color: '#999'}}>{new Date(s.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</span>
                      </td>
                      <td style={{padding: '8px', textAlign: 'right', verticalAlign: 'middle'}}>
                        <span style={badgeStyle(s.event_type)}>{s.event_type || 'point'}</span>
                      </td>
                    </tr>
                  ))}
                  {sessions.length === 0 && (
                    <tr><td colSpan="2" style={{padding: '10px', textAlign: 'center', color: '#999'}}>No logs found</td></tr>
                  )}
                </tbody>
              </table>
            )}
          </div>
        </div>
      )}
    </>
  );
};

// --- STYLES ---
const floatingButtonStyle = {
  position: "absolute", top: "20px", right: "20px", zIndex: 1000,
  padding: "12px 24px", backgroundColor: "#007bff", color: "white",
  border: "none", borderRadius: "30px", cursor: "pointer",
  boxShadow: "0 4px 12px rgba(0,0,0,0.2)", fontWeight: "bold",
  transition: "transform 0.2s ease"
};

const panelStyle = {
  position: "absolute", top: "80px", right: "20px", width: "320px",
  maxHeight: "80vh", backgroundColor: "rgba(255, 255, 255, 0.95)", 
  backdropFilter: "blur(10px)", borderRadius: "16px",
  boxShadow: "0 8px 32px rgba(0,0,0,0.15)", zIndex: 1000,
  overflowY: "auto", padding: "20px", fontFamily: "sans-serif",
  border: "1px solid rgba(255,255,255,0.3)"
};

const sectionStyle = { marginBottom: "20px", display: "flex", flexDirection: "column", gap: "8px" };
const labelStyle = { color: "#888", textTransform: "uppercase", fontSize: "10px", fontWeight: "bold", letterSpacing: '0.5px' };
const inputStyle = { padding: "10px", borderRadius: "8px", border: "1px solid #ddd", fontSize: '13px' };
const buttonGroupGrid = { display: "grid", gridTemplateColumns: "1fr 1fr", gap: "8px" };
const statCardStyle = { background: "#f8f9fa", padding: "12px", borderRadius: "12px", border: "1px solid #eee" };
const valueStyle = { fontSize: "18px", fontWeight: "bold", color: "#222" };
const tableWrapperStyle = { borderRadius: "8px", overflow: "hidden", border: "1px solid #eee" };

const filterButtonStyle = (active) => ({
  padding: "8px", borderRadius: "6px", cursor: "pointer", border: active ? "1px solid #007bff" : "1px solid #ddd",
  backgroundColor: active ? "#007bff" : "white", color: active ? "white" : "#555",
  fontSize: "12px", fontWeight: "600", transition: 'all 0.2s'
});

const clearButtonStyle = {
  marginTop: "8px", padding: "8px", backgroundColor: "transparent", color: "#d93025",
  border: "1px solid #fad2cf", borderRadius: "6px", cursor: "pointer", fontSize: "11px", fontWeight: '600'
};

const badgeStyle = (type) => ({
  fontSize: '9px', padding: '3px 8px', borderRadius: '12px', textTransform: 'uppercase', fontWeight: '700',
  backgroundColor: type === 'logout' ? '#fff0f0' : '#e6f4ea', color: type === 'logout' ? '#d93025' : '#1e8e3e'
});

export default DashboardMap;